import { baseThemeContract, vars } from '../../../vanilla/vars.css'
import { ArrowContainer, Popover, PopoverPosition } from 'react-tiny-popover'
import { Box, Flex, Stack, Text } from '../../../vanilla'
import type { Sprinkles } from '../../../vanilla/sprinkles.css'
import { CheckIcon, InfoIcon, CloseIcon, VanMotionIcon, StarMotionIcon, RapidBasketIcon } from '../../icons'
import React, { useState, useEffect } from 'react'
import { ProgressBar } from '../../progress-bar'
import { Link } from '../../link'
import { BasketThreshold } from '../../../types/store/basket'
import { handleClosePopoverEvents, isMobile } from '../../../utils/utils'

type ProgressBarColor = Sprinkles['color']

const applyProgressColour = (color?: string | null, fallback: keyof typeof vars.color = 'accent1'): ProgressBarColor => {
  if (color && color in vars.color) {
    return color as keyof typeof vars.color
  }

  return fallback
}

interface BasketThresholdProgressBarProps {
  basketThreshold: BasketThreshold,
  border: boolean
}

export const BasketThresholdProgressBar = ({
  basketThreshold,
  border,
}: BasketThresholdProgressBarProps) => {
  const [popoverTextOpen, setPopoverTextOpen] = useState(false)
  const [popoverBarOpen, setPopoverBarOpen] = useState(false)

  const toggleTextPopover = () => {
    setPopoverTextOpen(!popoverTextOpen)
    setPopoverBarOpen(false)
  }

  const getProgressIcon = (svg: string | null) => {
    switch (svg) {
      case 'rapid-basket':
        return RapidBasketIcon
      case 'star-motion':
        return StarMotionIcon
      case 'van-motion':
      default:
        return VanMotionIcon
    }
  }

  return (
    <Stack width="full" {...(border && { padding: "16px", spacing: "12px", borderTop: "1px", borderColor: "gray200" })}>
        <Flex alignSelf="center" align="center" gap="8px">
          <Text as="span" variant="text3" data-test-selector="promo-threshold-progress-text">
            {basketThreshold.complete ? basketThreshold.completedText : basketThreshold.progressText}
          </Text>
          <BasketThresholdPopover
            onClose={() => setPopoverTextOpen(false)}
            basketThreshold={basketThreshold}
            isOpen={popoverTextOpen}
            positions="top"
          >
            <InfoIcon className='promo-threshold-popover' data-test-selector="promo-threshold-info-icon" boxSize="16px" onClick={() => toggleTextPopover()} />
          </BasketThresholdPopover>
        </Flex>
      <BasketThresholdPopover
        onClose={() => setPopoverBarOpen(false)}
        basketThreshold={basketThreshold}
        isOpen={popoverBarOpen}
        positions="top"
      >
        <Box width="full" onClick={() => setPopoverBarOpen((prev) => !prev)}>
          <ProgressBar
            color={applyProgressColour(basketThreshold.barColour, 'promotionColor')}
            reachedColor={applyProgressColour(basketThreshold.barColour, 'accent1')}
            progressValue={Number(basketThreshold.progress)}
            icon={getProgressIcon(basketThreshold.svg)}
            data-test-selector={`${basketThreshold.complete ? 'promo-threshold-progress-bar-complete' : 'promo-threshold-progress-bar-incomplete'}`}
            reachedContent={
              <Flex as="span" gap="4px" flexGrow={'1'} align="center" justifyContent='center'>
                {!basketThreshold.barText && <CheckIcon color="white" boxSize="12px" />}
                <Text variant="text0" textAlign='center' color="white">
                  {!basketThreshold.barText ? basketThreshold.completedText : basketThreshold.barText}
                </Text>
              </Flex>
            }
          />
        </Box>
      </BasketThresholdPopover>
    </Stack>
  )
}

interface BasketThresholdPopoverProps {
  basketThreshold: BasketThreshold
  isOpen: boolean
  onClose: () => void
  children: React.ReactElement
  positions: PopoverPosition
}

const BasketThresholdPopover = ({ basketThreshold, isOpen, onClose, positions, children }: BasketThresholdPopoverProps) => {
  useEffect(() => {
    handleClosePopoverEvents(isOpen, onClose, 'promo-threshold-popover')
  }, [isOpen, onClose]);

  return (
    <Popover
      positions={[positions]}
      align="center"
      reposition={true}
      containerStyle={{ zIndex: baseThemeContract.zIndex.popover }}
      onClickOutside={onClose}
      isOpen={isOpen}
      content={({ position, childRect, popoverRect }) => (
        <Stack>
          <ArrowContainer
            position={position}
            childRect={childRect}
            popoverRect={popoverRect}
            arrowColor="white"
            arrowSize={10}
            arrowStyle={{ zIndex: baseThemeContract.zIndex.popover }}
          >
            <Stack
              spacing="16px"
              paddingX="20px"
              paddingTop="20px"
              paddingBottom="32px"
              bg="white"
              style={{
                maxWidth: isMobile() ? "100vw" : 420,
                width: isMobile() ? "calc(100vw - 20px)" : "auto",
                boxShadow: "0 10px 25px 0 rgb(0 0 0 / 40%)",
              }}
              className='promo-threshold-popover'
            >
              <Flex align="center" justify="space-between" gap="4px">
                <Text variant="heading0">{basketThreshold.popoverTitle}</Text>
                <CloseIcon marginLeft="8px" boxSize="30px" onClick={onClose} />
              </Flex>
              <Text variant="text3" lineHeight="short">
                {basketThreshold.popoverText}
              </Text>
              {basketThreshold.popoverLink && basketThreshold.popoverLinkText && (
                <Text
                  variant="text3"
                  to={basketThreshold.popoverLink}
                  as={Link}
                  color="accent3"
                  textDecoration="underline"
                >
                  {basketThreshold.popoverLinkText}
                </Text>
              )}
            </Stack>
          </ArrowContainer>
        </Stack>
      )}
    >
      {children}
    </Popover>
  )
}
